import Pusher from 'pusher-js/react-native';
import {setSuccess, setError, setActionAuthentication, getActionAuthentication, setToken} from "../../reducers/configSlice";
import {STORAGE} from "../storage";
import {dispatchAction} from "../state/dispatchAction";
import { EventBus } from "../EventBus";
import { Store } from "../../reducers/store";
import { fetchDeviceConfig } from "../config/configService";
import { fetchDeviceBookingList } from "../bookingList";
import { fetchDeviceMaintenance } from "../maintenance";
import { extractDeviceIdFromToken } from "../helpers/extractDeviceIdFromToken";
import { initSoketiBookingInterval, stopSoketiBookingInterval } from "./soketiBookingInterval";
import { initSoketiMaintenanceInterval, stopSoketiMaintenanceInterval } from "./soketiMaintenanceInterval";
import {
    SOKETI_APP_KEY,
    SOKETI_CLUSTER,
    SOKETI_DEFAULT_WS_PORT,
    SOKETI_DEFAULT_WSS_PORT,
    DEVICE_CHANNEL_PREFIX,
    SPACE_CHANNEL_PREFIX,
    FLOOR_CHANNEL_PREFIX,
    LOCATION_CHANNEL_PREFIX,
    SPACE_GROUP_CHANNEL_PREFIX,
    SPACE_TYPE_CHANNEL_PREFIX,
    DEVICE_EVENTS,
    BOOKING_EVENTS,
    LOCATION_EVENTS,
    MAINTENANCE_EVENTS,
    SPACE_GROUP_EVENTS,
    SPACE_TYPE_EVENTS,
    CONNECTION_STATES,
    INTERNAL_EVENTS
} from "./constants";

let pusher = null;
let deviceChannel = null;
let spaceChannel = null;
let floorChannel = null;
let locationChannel = null;
let spaceGroupChannel = null;
let spaceTypeChannel = null;

// Debounced maintenance fetch to prevent multiple requests
let maintenanceFetchTimeout = null;
const debouncedMaintenanceFetch = () => {
    if (maintenanceFetchTimeout) {
        clearTimeout(maintenanceFetchTimeout);
    }

    maintenanceFetchTimeout = setTimeout(async () => {
        await fetchDeviceMaintenance();
        maintenanceFetchTimeout = null;
    }, 100); // 100ms debounce
};

export const initSoketiService = async () => {
    const soketiUrl = await STORAGE.get("soketiUrl");
    const baseUrl = await STORAGE.get("baseUrl");
    const token = await STORAGE.get("token");

    if (!soketiUrl || !token || !baseUrl) return false;

    const deviceId = extractDeviceIdFromToken(token);
    if (!deviceId) return false;

    await STORAGE.set('deviceId', deviceId);

    try {
        // Create Pusher connection
        const soketiHost = baseUrl.replace('/api', '').replace('https://', '').replace('http://', '');
        const forceTLS = baseUrl.startsWith('https://');

        pusher = new Pusher(SOKETI_APP_KEY, {
            cluster: SOKETI_CLUSTER,
            wsPort: SOKETI_DEFAULT_WS_PORT,
            wssPort: SOKETI_DEFAULT_WSS_PORT,
            enabledTransports: ['ws', 'wss'],
            disableStats: true,
            activityTimeout: 120000,
            pongTimeout: 30000,
            httpPort: forceTLS ? 443 : 80,
            httpsPort: 443,
            httpHost: soketiHost,
            httpPath: soketiUrl,
            wsHost: soketiHost,
            wsPath: soketiUrl,
            forceTLS: forceTLS,
            encrypted: forceTLS
        });

        // Setup connection handlers
        pusher.connection.bind(CONNECTION_STATES.CONNECTED, onConnected);
        pusher.connection.bind(CONNECTION_STATES.DISCONNECTED, () => {
            EventBus.emit(INTERNAL_EVENTS.SOKETI_DISCONNECTED);
            // Emit for RASP monitoring
            EventBus.emit('soketi_event', {
                type: 'disconnect',
                timestamp: Date.now()
            });
        });
        pusher.connection.bind('error', (error) => {
            EventBus.emit(INTERNAL_EVENTS.SOKETI_ERROR, error);
            // Emit for RASP monitoring
            EventBus.emit('soketi_event', {
                type: 'error',
                error: error.message || 'Unknown error',
                timestamp: Date.now()
            });
        });

        // Subscribe to device channel
        deviceChannel = pusher.subscribe(`${DEVICE_CHANNEL_PREFIX}${deviceId}`);
        deviceChannel.bind(DEVICE_EVENTS.CONFIG_UPDATED, () => onConfigUpdated(true));
        deviceChannel.bind(DEVICE_EVENTS.DELETED, onDeviceDeleted);
        deviceChannel.bind(DEVICE_EVENTS.FORCE_DISCONNECT, onDeviceDeleted);
        deviceChannel.bind(DEVICE_EVENTS.DISCONNECT, onDeviceDeleted);
        deviceChannel.bind(DEVICE_EVENTS.RESTART, onDeviceRestart);
        deviceChannel.bind_global(onActionAuthorized);

        return true;
    } catch (error) {
        console.error('SOKETI - Connection failed:', error);
        return false;
    }
};

/**
 * Clean up Socket.IO artifacts when Soketi connection succeeds
 *
 * This ensures that once a client is upgraded to Soketi, all Socket.IO
 * related storage is cleaned up to prevent any potential fallback scenarios.
 *
 * NOTE: We keep socketUrl in storage because:
 * 1. setInitialAppData.js uses it for baseUrl derivation
 * 2. setInitialAppData.js checks it as essential data
 * 3. Wrapper.js uses it for reconnection logic
 *
 * The safety guards in Socket.IO service prevent actual Socket.IO usage.
 */
const cleanupSocketIOArtifacts = async () => {
    try {
        console.log('SOKETI - Cleaning up Socket.IO artifacts after successful connection');

        // Remove socketUrl from storage since Soketi clients will never use Socket.IO
        const socketUrl = await STORAGE.get("socketUrl");
        if (socketUrl) {
            console.log('SOKETI - Removing Socket.IO URL from storage:', socketUrl);
            await STORAGE.delete("socketUrl");
            console.log('SOKETI - Socket.IO URL removed from storage');
        }

        console.log('SOKETI - Socket.IO cleanup completed, client is now Soketi-only');

    } catch (error) {
        console.error('SOKETI - Error during Socket.IO cleanup:', error);
        // Don't throw - this is not critical for Soketi operation
    }
};

// Event handlers
const onConnected = async () => {
    EventBus.emit(INTERNAL_EVENTS.SOKETI_CONNECTED);

    // Emit for RASP monitoring
    EventBus.emit('soketi_event', {
        type: 'connect',
        timestamp: Date.now()
    });

    // Clean up Socket.IO artifacts when Soketi connection succeeds
    await cleanupSocketIOArtifacts();

    // Case A: Fetch config and setup channels
    const success = await fetchDeviceConfig();
    if (success) {
        setupAdditionalChannels();
        if (Store.getState().data?.config?.space_id) {
            // Fetch initial booking list after config is loaded
            await fetchDeviceBookingList();
            // Fetch initial maintenance status after config is loaded
            await fetchDeviceMaintenance();
        }
        // Start the Soketi booking interval for time-based checks
        initSoketiBookingInterval();
        // Start the Soketi maintenance interval for time-based checks
        initSoketiMaintenanceInterval();
    }
};

const onConfigUpdated = async (refetchBookingAndMaintenance = true) => {
    // Case B: Fetch config and setup channels
    const success = await fetchDeviceConfig();
    if (success) {
        setupAdditionalChannels();

        // Only fetch booking list and maintenance when space_id changes
        // For other changes (space_type, space_group, floor, location), only config is refetched
        if (refetchBookingAndMaintenance) {
            // Fetch updated booking list after config is updated
            await fetchDeviceBookingList();
            // Fetch updated maintenance status after config is updated
            await fetchDeviceMaintenance();
        }
    }
};

// Config-only update handler (for space_type, space_group, floor, location changes)
const onConfigOnlyUpdated = async () => {
    await onConfigUpdated(false);
};

const onTokenRefreshed = async (data) => {
    if (data.data) {
        // Update storage (same as Socket.IO)
        await STORAGE.set("token", data.data);

        // Update Redux state (same as Socket.IO)
        dispatchAction(setToken(data.data));
    }
};

const onDeviceDeleted = () => {
    EventBus.emit('destroyInstance');
};

const onDeviceRestart = () => {
    EventBus.emit('restartApp');
};

// Store for action-specific callbacks and handlers
let actionAuthHandlers = new Map();

const onActionAuthorized = async (eventName, data) => {
    if (!eventName.startsWith(DEVICE_EVENTS.ACTION_AUTHORIZED)) return;

    const actionCode = eventName.replace(DEVICE_EVENTS.ACTION_AUTHORIZED + '_', '');
    const handler = actionAuthHandlers.get(actionCode);
    const state = Store.getState();
    const currentActionAuth = getActionAuthentication(state);

    dispatchAction(setActionAuthentication(null));

    if (data?.status === 1) {
        if (handler && handler.callback) {
            handler.callback();
        }

        let successMessage = data?.message;
        if (currentActionAuth && currentActionAuth.successMessage) {
            successMessage = data?.message + '. ' + currentActionAuth.successMessage;
        }

        dispatchAction(setSuccess(successMessage));
    } else {
        dispatchAction(setError(data?.message ? data.message : 'Authorization failed'));
    }

    actionAuthHandlers.delete(actionCode);
};

// Soketi equivalent of addActionAuthenticationListener
export const addSoketiActionAuthenticationListener = (action, callback = null) => {
    if (!pusher || !deviceChannel) {
        console.warn('SOKETI - Cannot add action listener: not connected');
        return;
    }

    // Store the callback for this action code
    actionAuthHandlers.set(action.code, { callback });

    // The global listener (onActionAuthorized) will handle the event when it arrives
    // No need to bind specific events since we use bind_global
};

// Setup space, floor, and location channels based on current config
const setupAdditionalChannels = () => {
    const config = Store.getState().data.config;
    if (!config || !pusher) return;

    // Handle space channel
    const newSpaceChannelName = config.space_id ? `${SPACE_CHANNEL_PREFIX}${config.space_id}` : null;
    if (newSpaceChannelName !== spaceChannel?.name) {
        if (spaceChannel) {
            spaceChannel.unbind_all();
            pusher.unsubscribe(spaceChannel.name);
        }

        if (newSpaceChannelName) {
            spaceChannel = pusher.subscribe(newSpaceChannelName);
            bindSpaceEvents(spaceChannel);
            bindMaintenanceEvents(spaceChannel);
        } else {
            spaceChannel = null;
        }
    }

    // Handle floor channel
    const newFloorChannelName = config.floor_id ? `${FLOOR_CHANNEL_PREFIX}${config.floor_id}` : null;
    if (newFloorChannelName !== floorChannel?.name) {
        if (floorChannel) {
            floorChannel.unbind_all();
            pusher.unsubscribe(floorChannel.name);
        }

        if (newFloorChannelName) {
            floorChannel = pusher.subscribe(newFloorChannelName);
            bindMaintenanceEvents(floorChannel);
        } else {
            floorChannel = null;
        }
    }

    // Handle location channel
    const newLocationChannelName = config.location_id ? `${LOCATION_CHANNEL_PREFIX}${config.location_id}` : null;
    if (newLocationChannelName !== locationChannel?.name) {
        if (locationChannel) {
            locationChannel.unbind_all();
            pusher.unsubscribe(locationChannel.name);
        }

        if (newLocationChannelName) {
            locationChannel = pusher.subscribe(newLocationChannelName);
            locationChannel.bind(LOCATION_EVENTS.UPDATED, onConfigOnlyUpdated);
            bindMaintenanceEvents(locationChannel);
        } else {
            locationChannel = null;
        }
    }

    // Handle space group channel
    const newSpaceGroupChannelName = config.space_group_id ? `${SPACE_GROUP_CHANNEL_PREFIX}${config.space_group_id}` : null;
    if (newSpaceGroupChannelName !== spaceGroupChannel?.name) {
        if (spaceGroupChannel) {
            spaceGroupChannel.unbind_all();
            pusher.unsubscribe(spaceGroupChannel.name);
        }

        if (newSpaceGroupChannelName) {
            spaceGroupChannel = pusher.subscribe(newSpaceGroupChannelName);
            spaceGroupChannel.bind(SPACE_GROUP_EVENTS.UPDATED, onConfigOnlyUpdated);
        } else {
            spaceGroupChannel = null;
        }
    }

    // Handle space type channel
    const newSpaceTypeChannelName = config.space_type_id ? `${SPACE_TYPE_CHANNEL_PREFIX}${config.space_type_id}` : null;
    if (newSpaceTypeChannelName !== spaceTypeChannel?.name) {
        if (spaceTypeChannel) {
            spaceTypeChannel.unbind_all();
            pusher.unsubscribe(spaceTypeChannel.name);
        }

        if (newSpaceTypeChannelName) {
            spaceTypeChannel = pusher.subscribe(newSpaceTypeChannelName);
            spaceTypeChannel.bind(SPACE_TYPE_EVENTS.UPDATED, onConfigOnlyUpdated);
        } else {
            spaceTypeChannel = null;
        }
    }
};

// Bind booking events on space channel
const bindSpaceEvents = (channel) => {
    BOOKING_EVENTS.forEach(eventName => {
        channel.bind(eventName, async (data) => {
            // Trigger booking list refresh via API call
            await fetchDeviceBookingList();
        });
    });
};

// Bind maintenance events on space, floor, and location channels
const bindMaintenanceEvents = (channel) => {
    channel.bind(MAINTENANCE_EVENTS.UPDATED, async (data) => {
        // Use debounced fetch to prevent multiple requests if event comes on multiple channels
        debouncedMaintenanceFetch();
    });
};

export const disconnectSoketi = () => {
    // Stop the intervals first
    stopSoketiBookingInterval();
    stopSoketiMaintenanceInterval();

    // Clear any pending maintenance fetch timeout
    if (maintenanceFetchTimeout) {
        clearTimeout(maintenanceFetchTimeout);
        maintenanceFetchTimeout = null;
    }

    if (deviceChannel) {
        deviceChannel.unbind_all();
        pusher?.unsubscribe(deviceChannel.name);
        deviceChannel = null;
    }
    if (spaceChannel) {
        spaceChannel.unbind_all();
        pusher?.unsubscribe(spaceChannel.name);
        spaceChannel = null;
    }
    if (floorChannel) {
        floorChannel.unbind_all();
        pusher?.unsubscribe(floorChannel.name);
        floorChannel = null;
    }
    if (locationChannel) {
        locationChannel.unbind_all();
        pusher?.unsubscribe(locationChannel.name);
        locationChannel = null;
    }
    if (spaceGroupChannel) {
        spaceGroupChannel.unbind_all();
        pusher?.unsubscribe(spaceGroupChannel.name);
        spaceGroupChannel = null;
    }
    if (spaceTypeChannel) {
        spaceTypeChannel.unbind_all();
        pusher?.unsubscribe(spaceTypeChannel.name);
        spaceTypeChannel = null;
    }
    if (pusher) {
        pusher.disconnect();
        pusher = null;
    }
};

export const reconnectSoketi = async () => {
    if (pusher?.connection.state === CONNECTION_STATES.CONNECTED) return true;
    if (pusher) {
        pusher.connect();
        return true;
    }
    return await initSoketiService();
};

export const isSoketiConnected = () => {
    return pusher?.connection.state === CONNECTION_STATES.CONNECTED;
};

export default {
    initSoketiService,
    reconnectSoketi,
    disconnectSoketi,
    isSoketiConnected
};
